import type { IntlShape, MessageDescriptor } from 'react-intl'
import { defineMessages } from 'react-intl'
import {
  DATES_REQUEST_ALREADY_EXISTS_CODE,
  DATES_REQUEST_OUTSIDE_POLICY_CODE,
  DATES_REQUEST_OUTSIDE_POLICY_NOT_AVAILABLE_CODE,
} from 'constants/orderErrorCodes'

interface DatesRequestErrorLike {
  code?: string
  message?: string
  errors?: DatesRequestErrorLike | Array<DatesRequestErrorLike | string | null> | string | null
}

const messages = defineMessages({
  datesRequestAlreadyExists: {
    id: 'ZjQsgP0vSy9PHCbZRuMu',
    defaultMessage: 'A request for these dates already exists for this booking. Please check your order for the latest status.',
    description: 'Error message shown when a customer submits a duplicate dates request for the same booking in checkout or my escapes flows.',
  },
  datesRequestOutsidePolicy: {
    id: 'AMR-wdHC9gNXXcl1gAaG',
    defaultMessage: 'These dates are outside the self-service policy for this booking. Please contact our support team for help with your request.',
    description: 'Error message shown when a customer submits a dates request that can no longer be handled within the normal self-service date policy.',
  },
  datesRequestOutsidePolicyNotAvailable: {
    id: '866X7caW4v83k_UyXNGD',
    defaultMessage: 'Outside-policy date changes are not available for this booking. Please contact our support team for help with your request.',
    description: 'Error message shown when outside-policy date change requests are disabled for this booking.',
  },
})

const fmt = (intl: IntlShape | undefined, descriptor: MessageDescriptor): string =>
  intl ? intl.formatMessage(descriptor) : descriptor.defaultMessage as string

const getNestedErrorCode = (
  error: DatesRequestErrorLike | undefined,
): string | undefined => {
  const nestedErrors = error?.errors

  if (!nestedErrors) {
    return undefined
  }

  if (Array.isArray(nestedErrors)) {
    const nestedErrorWithCode = nestedErrors.find(
      nestedError => typeof nestedError === 'object' && !!nestedError?.code,
    )

    return typeof nestedErrorWithCode === 'object' ? nestedErrorWithCode?.code : undefined
  }

  return typeof nestedErrors === 'object' ? nestedErrors.code : undefined
}

const getDatesRequestErrorCode = (
  error: DatesRequestErrorLike | undefined,
): string | undefined => error?.code ?? getNestedErrorCode(error)

export default function getDatesRequestErrorMessage(
  error: DatesRequestErrorLike | undefined,
  fallbackMessage: string,
  intl?: IntlShape,
): string {
  switch (getDatesRequestErrorCode(error)) {
    case DATES_REQUEST_ALREADY_EXISTS_CODE:
      return fmt(intl, messages.datesRequestAlreadyExists)
    case DATES_REQUEST_OUTSIDE_POLICY_CODE:
      return fmt(intl, messages.datesRequestOutsidePolicy)
    case DATES_REQUEST_OUTSIDE_POLICY_NOT_AVAILABLE_CODE:
      return fmt(intl, messages.datesRequestOutsidePolicyNotAvailable)
    default:
      return fallbackMessage
  }
}
